import { generateNanoId } from "@/shared";
import type { ITableCell, ITableSettings } from "../types";
import { cellTemplateHtml, formatCellValue } from "./format-cell-value";
import { tableNormalizeColWidths } from "./table-normalize-col-widths";

/**
 * Applies a `data`-type variable's 2D matrix to a WHOLE table.
 *
 * Distinct from `parseTableListDynamicVar`, which clones a single template row
 * per record and substitutes `[Key]` placeholders — that's the *list* variable's
 * mechanism. Here the variable owns the entire table: its matrix replaces the
 * cells, row count, column count and column widths outright, so the sender can
 * paste/upload a sheet of any size without the author having to pre-build a
 * matching grid.
 *
 * Cell *styling* is preserved by reusing the template cell at the same
 * position, or — past the author's grid — the same column of its last row (the
 * last column of the row for extra columns), so extra rows carry the body
 * formatting, not the header's. The value is written through that cell's text
 * formatting; a URL becomes a link (see format-cell-value.ts, shared with the
 * editor's imports and "Make static"). A matrix cell is plain text from a sheet
 * or an API payload, so markup in it stays inert.
 */

export const parseTableDataDynamicVar = ({
  settings,
  matrix,
}: {
  settings: ITableSettings;
  matrix: unknown;
}): ITableSettings => {
  if (!Array.isArray(matrix) || matrix.length === 0) return settings;

  const rows: string[][] = matrix
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) => row.map((cell) => (cell == null ? "" : String(cell))));
  if (!rows.length) return settings;

  const tableCols = rows.reduce(
    (widest, row) => Math.max(widest, row.length),
    0,
  );
  if (tableCols === 0) return settings;
  const tableRows = rows.length;

  const templateCells = settings.tableData ?? [];
  const templateCols = settings.tableCols || 1;
  const templateRows = Math.max(
    1,
    Math.ceil(templateCells.length / templateCols),
  );
  // Style source for a target position, clamped into the author's grid. A cell
  // merged into another holds no formatting of its own: use the one above it.
  const styleFor = (row: number, column: number): ITableCell | undefined => {
    const col = Math.min(column, templateCols - 1);
    for (let r = Math.min(row, templateRows - 1); r >= 0; r -= 1) {
      const cell = templateCells[r * templateCols + col];
      if (cell && !cell.mergedInto) return cell;
    }
    return templateCells[0];
  };

  const tableData: ITableCell[] = [];
  for (let row = 0; row < tableRows; row += 1) {
    for (let column = 0; column < tableCols; column += 1) {
      const style = styleFor(row, column);
      tableData.push({
        ...(style ?? { colSpan: 1, rowSpan: 1, content: "" }),
        id: generateNanoId(),
        // A matrix cell is plain text and never spans — merges belong to the
        // author's grid, which this replaces.
        colSpan: 1,
        rowSpan: 1,
        mergedInto: undefined,
        embedded: undefined,
        calculation: undefined,
        groupId: undefined,
        group: undefined,
        content: formatCellValue(
          cellTemplateHtml(style),
          rows[row][column] ?? "",
        ),
      });
    }
  }

  const existingWidths = settings.tableColWidths ?? [];
  const tableColWidths = tableNormalizeColWidths(
    Array.from(
      { length: tableCols },
      (_, column) => existingWidths[column] ?? 100 / tableCols,
    ),
  );

  return {
    ...settings,
    tableData,
    tableRows,
    tableCols,
    tableColWidths,
    // Row heights were sized for the author's grid; let the new rows auto-size.
    tableRowHeights: [],
  };
};
